import { Box, Paper, Stack, Tooltip, Typography } from '@mui/material'
import type { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid'
import { DateTime } from 'luxon'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import CustomChip from '@/components/dataDisplay/CustomChip'
import CustomDataGrid from '@/components/dataDisplay/CustomDataGrid'
import EllipsisTypography from '@/components/dataDisplay/EllipsisTypography'
import { PrequalificationRouteInformation } from '@/features/prequalification/constants'
import LocalPqResourceSetsEmptyState from '@/features/prequalification/localControl/components/LocalPqResourceSetsEmptyState'
import LocalPqResourceSetsErrorState from '@/features/prequalification/localControl/components/LocalPqResourceSetsErrorState'
import LocalPqResourceSetsNoResults from '@/features/prequalification/localControl/components/LocalPqResourceSetsNoResults'
import LocalPqResourceSetsToolbar from '@/features/prequalification/localControl/components/LocalPqResourceSetsToolbar'
import PqPhaseChip from '@/features/prequalification/localControl/components/PqPhaseChip'
import { useLocalPqMarketsQuery } from '@/features/prequalification/localControl/hooks/useLocalPqMarketsQuery'
import { useLocalPqResourceSetsQuery } from '@/features/prequalification/localControl/hooks/useLocalPqResourceSetsQuery'
import type { PqMarketCatalogueEntry } from '@/features/prequalification/localControl/types'
import { marketLabel } from '@/features/prequalification/localControl/utils/marketCatalogue'
import { getPhaseChipProps } from '@/features/prequalification/localControl/utils/phasePresentation'
import type { ResourceSetFilters } from '@/features/prequalification/localControl/utils/resourceSetFilters'
import {
  DEFAULT_RESOURCE_SET_FILTERS,
  filterResourceSets,
} from '@/features/prequalification/localControl/utils/resourceSetFilters'
import type { PqMarketStatus, PrequalificationResourceSet } from '@/features/prequalification/types'

const DATE_FORMAT = 'dd LLL yyyy, HH:mm'
/** The standard density's row height, kept as the floor now that rows grow to fit wrapped market chips. */
const MIN_ROW_HEIGHT_PX = 52

type ResourceSetRenderCell = GridRenderCellParams<PrequalificationResourceSet>

/**
 * Per-market outcome chips: a chip per market the resource set has runs in, coloured
 * by that market's aggregate phase (passed / running / failed / …) with the
 * phase spelled out in the tooltip. Communicates progress, not mere attempt.
 */
function MarketChips({
  markets,
  catalogue,
}: Readonly<{ markets: PqMarketStatus[]; catalogue: PqMarketCatalogueEntry[] | null }>) {
  const { t } = useTranslation()

  if (markets.length === 0) {
    return (
      <Stack sx={{ justifyContent: 'center', height: '100%' }}>
        <Typography color="text.secondary" variant="body2">
          —
        </Typography>
      </Stack>
    )
  }

  return (
    <Stack sx={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, py: 1 }}>
      {markets.map(({ market, phase }) => {
        const { label, color, icon, inactive } = getPhaseChipProps(phase, t)
        return (
          <Tooltip key={market} title={label}>
            <span>
              <CustomChip
                color={inactive ? undefined : color}
                icon={icon}
                label={marketLabel(catalogue, market)}
                size="small"
                variant={inactive ? 'outlinedInactive' : 'outlined'}
              />
            </span>
          </Tooltip>
        )
      })}
    </Stack>
  )
}

/** The resource set name — the row's primary identity. */
function ResourceSetNameCell({ row }: Readonly<ResourceSetRenderCell>) {
  return (
    <Stack sx={{ justifyContent: 'center', height: '100%', minWidth: 0 }}>
      <EllipsisTypography tooltipTitle={row.name} variant="body2">
        {row.name}
      </EllipsisTypography>
    </Stack>
  )
}

/** Most recently requested first; the list reads as a recency feed of resource sets. */
function byRecency(a: PrequalificationResourceSet, b: PrequalificationResourceSet): number {
  return b.requestedAt.toMillis() - a.requestedAt.toMillis()
}

function LocalPqResourceSetsDataGrid() {
  const { t } = useTranslation()
  const { resourceSets, isLoading, isError, refetch } = useLocalPqResourceSetsQuery()
  const { markets: catalogue } = useLocalPqMarketsQuery()
  const [filters, setFilters] = useState<ResourceSetFilters>(DEFAULT_RESOURCE_SET_FILTERS)

  const allRows = useMemo(() => [...(resourceSets ?? [])].sort(byRecency), [resourceSets])
  const rows = useMemo(() => filterResourceSets(allRows, filters, DateTime.now()), [allRows, filters])

  const columns: GridColDef<PrequalificationResourceSet>[] = [
    {
      field: 'name',
      headerName: t('prequalifications.list.header.resource_set'),
      display: 'flex',
      flex: 1.4,
      renderCell: (params: ResourceSetRenderCell) => <ResourceSetNameCell {...params} />,
    },
    {
      field: 'resourceCount',
      headerName: t('prequalifications.list.header.resources'),
      display: 'flex',
      flex: 0.7,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'markets',
      headerName: t('prequalifications.list.header.markets'),
      display: 'flex',
      flex: 1.8,
      sortable: false,
      renderCell: ({ row }: ResourceSetRenderCell) => <MarketChips catalogue={catalogue} markets={row.markets} />,
    },
    {
      field: 'phase',
      headerName: t('prequalifications.list.header.phase'),
      display: 'flex',
      flex: 1,
      renderCell: ({ row }: ResourceSetRenderCell) => <PqPhaseChip phase={row.phase} size="small" />,
    },
    {
      field: 'requestedAt',
      headerName: t('prequalifications.list.header.created'),
      display: 'flex',
      flex: 1.4,
      valueFormatter: (value: PrequalificationResourceSet['requestedAt']) => value?.toFormat(DATE_FORMAT) ?? '-',
    },
    {
      field: 'lastStatusAt',
      headerName: t('prequalifications.list.header.updated'),
      display: 'flex',
      flex: 1.4,
      valueFormatter: (value: PrequalificationResourceSet['lastStatusAt']) => value?.toFormat(DATE_FORMAT) ?? '-',
    },
  ]

  const clickableRows = {
    navigateTo: ({ row }: GridRowParams<PrequalificationResourceSet>) =>
      PrequalificationRouteInformation.RESOURCE_SET_DETAILS.navigationPath(row.id),
  }

  // First run (or initial load): no toolbar. A failed fetch shows an error state with a
  // retry — never the empty state, which would falsely read as "you have no resource sets".
  if (isLoading || isError || allRows.length === 0) {
    const noRowsOverlay = isError
      ? () => <LocalPqResourceSetsErrorState onRetry={() => void refetch()} />
      : LocalPqResourceSetsEmptyState
    return (
      <CustomDataGrid
        aria-label={t('prequalifications.list.table_label')}
        clickableRows={clickableRows}
        columns={columns}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        rows={[]}
        slots={{ noRowsOverlay }}
        // With autoHeight + zero rows the grid body collapses and the overlay
        // renders behind the column headers; give the overlay a real height.
        sx={{ '--DataGrid-overlayHeight': '360px' }}
      />
    )
  }

  return (
    <Paper sx={{ mt: 3, mb: 3, overflow: 'hidden' }}>
      <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
        <LocalPqResourceSetsToolbar filters={filters} onFiltersChange={setFilters} />
      </Box>

      <CustomDataGrid
        aria-label={t('prequalifications.list.table_label')}
        clickableRows={clickableRows}
        columns={columns}
        getRowHeight={() => 'auto'}
        getRowId={(row) => row.id}
        includeWrapper={false}
        initialState={{ sorting: { sortModel: [{ field: 'requestedAt', sort: 'desc' }] } }}
        rows={rows}
        slots={{
          noRowsOverlay: () => (
            <LocalPqResourceSetsNoResults onClear={() => setFilters(DEFAULT_RESOURCE_SET_FILTERS)} />
          ),
        }}
        // Keep the "no results" overlay clear of the column headers when filters exclude every row.
        sx={{ '--DataGrid-overlayHeight': '240px', '& .MuiDataGrid-cell': { minHeight: MIN_ROW_HEIGHT_PX } }}
      />
    </Paper>
  )
}

export default LocalPqResourceSetsDataGrid
